import type { Router } from 'vue-router'

const RELOAD_STORAGE_KEY = 'stale-chunk-reload-at'
const RELOAD_COOLDOWN_MS = 30_000

let isReloading = false

// preventDefault() makes Vite's preload helper resolve `undefined`, so the navigation that triggered the reload
// rejects with a vue-router "Couldn't resolve component" error. Telemetry uses this to ignore that noise.
export function isReloadingForStaleChunk(): boolean {
  return isReloading
}

function readLastReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_STORAGE_KEY)) || 0
  } catch {
    return 0
  }
}

function markReload(): void {
  try {
    sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()))
  } catch {
    // Storage unavailable - cooldown is best effort.
  }
}

// After a deploy, old hashed chunks no longer exist. Reload once to pick up the new index.html.
// Within the cooldown the error is left to propagate, so a genuinely broken chunk can't cause a reload loop.
// If a route chunk failed mid-navigation, the reload lands on the route the user clicked.
export function reloadOnStaleChunk(router: Router): void {
  let pendingTarget: string | null = null

  router.beforeEach(to => {
    pendingTarget = to.fullPath
  })
  router.afterEach(() => {
    pendingTarget = null
  })
  router.onError(() => {
    pendingTarget = null
  })

  window.addEventListener('vite:preloadError', event => {
    if (Date.now() - readLastReloadAt() < RELOAD_COOLDOWN_MS) return

    event.preventDefault()
    markReload()
    isReloading = true

    if (pendingTarget) {
      window.history.replaceState(window.history.state, '', router.resolve(pendingTarget).href)
    }
    window.location.reload()
  })
}
